import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Settings = () => {
  const [settings, setSettings] = useState({
    showApiStatus: true,
    showQuickLinks: true,
    showDocumentation: true,
    showDocumentManager: true,
    showPasswordManager: true
  });

  useEffect(() => {
    // Load settings from localStorage
    const savedSettings = localStorage.getItem('hubWidgetSettings');
    if (savedSettings) {
      try {
        setSettings(JSON.parse(savedSettings));
      } catch (error) {
        console.error('Failed to parse saved settings:', error);
      }
    }
  }, []);

  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('hubWidgetSettings', JSON.stringify(newSettings));
  };

  const resetToDefaults = () => {
    const defaultSettings = {
      showApiStatus: true,
      showQuickLinks: true,
      showDocumentation: true,
      showDocumentManager: true,
      showPasswordManager: true
    };
    setSettings(defaultSettings);
    localStorage.setItem('hubWidgetSettings', JSON.stringify(defaultSettings));
  };

  const ToggleSwitch = ({ id, checked, onChange, label, description }) => (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div className="flex-1">
        <label htmlFor={id} className="text-sm font-medium text-gray-900 cursor-pointer">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        )}
      </div>
      <div className="ml-4">
        <button
          type="button"
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
            checked ? 'bg-blue-600' : 'bg-gray-200'
          }`}
          onClick={() => onChange(!checked)}
          id={id}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              checked ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-100 via-white to-sage-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <h1 className="text-2xl font-bold text-forest-800">
                Settings
              </h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          
          {/* Widget Visibility Section */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Widget Visibility
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Control which widgets are displayed on your dashboard
              </p>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <ToggleSwitch
                  id="apiStatus"
                  checked={settings.showApiStatus}
                  onChange={(value) => updateSetting('showApiStatus', value)}
                  label="API Status Monitor"
                  description="Shows real-time status of OpenAI, Claude, GitHub, Stripe, and other services"
                />

                <ToggleSwitch
                  id="quickLinks"
                  checked={settings.showQuickLinks}
                  onChange={(value) => updateSetting('showQuickLinks', value)}
                  label="Quick Links"
                  description="Displays configurable quick access links and shortcuts"
                />

                <ToggleSwitch
                  id="documentation"
                  checked={settings.showDocumentation}
                  onChange={(value) => updateSetting('showDocumentation', value)}
                  label="SEEN Documentation"
                  description="Browse and search through organized project documentation"
                />

                <ToggleSwitch
                  id="documentManager"
                  checked={settings.showDocumentManager}
                  onChange={(value) => updateSetting('showDocumentManager', value)}
                  label="Document Library"
                  description="Manage important files with inline preview capabilities"
                />

                <ToggleSwitch
                  id="passwordManager"
                  checked={settings.showPasswordManager}
                  onChange={(value) => updateSetting('showPasswordManager', value)}
                  label="Password Manager"
                  description="Secure storage and management of credentials"
                />
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={resetToDefaults}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Reset to Defaults
                </button>
              </div>
            </div>
          </div>

          {/* About Section */}
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                About Seen Place Hub
              </h2>
            </div>

            <div className="p-6">
              <div className="space-y-4 text-sm text-gray-600">
                <p>
                  <strong className="text-gray-900">Version:</strong> 2.0.0
                </p>
                <p>
                  <strong className="text-gray-900">Environment:</strong> {process.env.NODE_ENV || 'development'}
                </p>
                <p>
                  <strong className="text-gray-900">Features:</strong>
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Real-time API status monitoring</li>
                  <li>Organized documentation system</li>
                  <li>Document management with inline preview</li>
                  <li>PDF signing capabilities</li>
                  <li>Secure authentication via Google OAuth</li>
                </ul>
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-t border-gray-200 pt-8 text-center text-sm text-gray-500">
            <p>Seen Place Hub • Internal Use Only</p>
            <p className="mt-1">© {new Date().getFullYear()} Seen Place</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Settings;